package br.com.htms.inspeccaocampo.export

import br.com.htms.inspeccaocampo.data.efetivo.Datas
import br.com.htms.inspeccaocampo.data.ocorrencias.AcaoPlano
import br.com.htms.inspeccaocampo.data.ocorrencias.OcDatas
import br.com.htms.inspeccaocampo.data.ocorrencias.Ocorrencia
import br.com.htms.inspeccaocampo.data.ocorrencias.OcorrenciasDados

/** Gestão de Ocorrências: ocorrências + plano de ação + dashboard. */
object PlanilhaOcorrencias {
    fun build(d: OcorrenciasDados): ByteArray {
        val hoje = OcDatas.hoje()
        fun tipo(o: Ocorrencia) = d.tipo(o.prefixo)?.nome ?: o.tipo.ifBlank { "Sem tipo" }
        fun classe(o: Ocorrencia) = o.campo("classificacao").substringBefore(" -").ifBlank { "Não classificada" }
        fun num(s: String) = PlanKit.numero(s)
        fun comissao(o: Ocorrencia): String {
            val linhas = (1..6).map { n ->
                val nome = o.campo("com$n")
                val cargo = o.campo("cargo$n")
                when {
                    nome.isBlank() && cargo.isBlank() -> ""
                    cargo.isBlank() -> nome
                    nome.isBlank() -> cargo
                    else -> "$nome ($cargo)"
                }
            }.filter { it.isNotBlank() }
            return if (linhas.isNotEmpty()) linhas.joinToString("; ") else o.campo("comissao")
        }

        val ocs = d.ocorrencias.sortedByDescending { it.data }
        val cols = listOf(
            ColPlan("ID", 12), ColPlan("TIPO", 24), ColPlan("DATA", 12, TipoCol.DATA), ColPlan("MÊS", 10), ColPlan("HORA", 8),
            ColPlan("CLASSIFICAÇÃO", 34), ColPlan("CLASSE", 10), ColPlan("TÍPICO / TRAJETO", 12), ColPlan("ACIDENTE", 10),
            ColPlan("LOCAL", 26), ColPlan("EMPRESA", 18), ColPlan("ATIVIDADE", 28), ColPlan("AGENTE CAUSADOR", 22),
            ColPlan("COLABORADOR", 28), ColPlan("MATRÍCULA", 10), ColPlan("FUNÇÃO", 22), ColPlan("SEXO", 10),
            ColPlan("NATUREZA DA LESÃO", 20), ColPlan("PARTE DO CORPO", 18),
            ColPlan("DIAS DE AFASTAMENTO", 12, TipoCol.NUMERO), ColPlan("DIAS DEBITADOS", 12, TipoCol.NUMERO),
            ColPlan("CARACTERIZAÇÃO", 36), ColPlan("CONTRATO", 18), ColPlan("OBRA", 22),
            ColPlan("HORAS TRABALHADAS", 12), ColPlan("TEMPO DE EMPRESA", 18), ColPlan("IDADE", 8),
            ColPlan("ESCOLARIDADE", 28), ColPlan("ESTADO CIVIL", 16),
            ColPlan("RESP. ATIVIDADE", 24), ColPlan("CARGO RESP. ATIVIDADE", 22), ColPlan("ESTAVA NO LOCAL", 12),
            ColPlan("ENTREVISTA SUPERVISOR", 40), ColPlan("DESCRIÇÃO", 50), ColPlan("TESTEMUNHAS", 40),
            ColPlan("PERDAS", 34),
            ColPlan("MATERIAL RELAÇÃO", 12), ColPlan("MATERIAL ITENS", 28), ColPlan("MATERIAL AVALIAÇÃO", 28),
            ColPlan("MÉTODO RELAÇÃO", 12), ColPlan("MÉTODO ITENS", 28), ColPlan("MÉTODO AVALIAÇÃO", 28),
            ColPlan("MÁQUINA RELAÇÃO", 12), ColPlan("MÁQUINA ITENS", 28), ColPlan("MÁQUINA AVALIAÇÃO", 28),
            ColPlan("MÃO DE OBRA RELAÇÃO", 14), ColPlan("MÃO DE OBRA ITENS", 28), ColPlan("MÃO DE OBRA AVALIAÇÃO", 28),
            ColPlan("MEDIDA RELAÇÃO", 12), ColPlan("MEDIDA ITENS", 28), ColPlan("MEDIDA AVALIAÇÃO", 28),
            ColPlan("MEIO AMBIENTE RELAÇÃO", 14), ColPlan("MEIO AMBIENTE ITENS", 28), ColPlan("MEIO AMBIENTE AVALIAÇÃO", 28),
            ColPlan("CONCLUSÃO DA ANÁLISE", 40),
            ColPlan("STATUS DA INVESTIGAÇÃO", 16), ColPlan("DATA DA INVESTIGAÇÃO", 14, TipoCol.DATA), ColPlan("RESPONSÁVEL INVESTIGAÇÃO", 24),
            ColPlan("BARREIRAS QUE FALHARAM", 36), ColPlan("CAUSA DA FALHA", 36),
            ColPlan("CAUSA RAIZ", 16), ColPlan("CONCLUSÃO", 40), ColPlan("LIÇÕES APRENDIDAS", 34), ColPlan("DIVULGADO", 10),
            ColPlan("COMISSÃO", 40), ColPlan("OBSERVAÇÕES", 34),
            ColPlan("AÇÕES", 8, TipoCol.NUMERO), ColPlan("AÇÕES ABERTAS", 10, TipoCol.NUMERO), ColPlan("AÇÕES VENCIDAS", 10, TipoCol.NUMERO),
            ColPlan("FOTOS", 8, TipoCol.NUMERO), ColPlan("REGISTRADO POR", 18), ColPlan("REGISTRADO EM", 13, TipoCol.DATA)
        )
        val linhas = ocs.map { o ->
            val data = PlanData.de(o.data)
            val acoes = d.acoesDe(o.id)
            listOf(
                o.id, tipo(o), data, PlanData.mes(data), o.campo("hora"),
                o.campo("classificacao"), classe(o), o.campo("tipoAcid"), if (o.ehAcidenteAtual(tipo(o))) "Sim" else "Não",
                o.local, o.campo("empresa"), o.campo("atividade"), o.campo("agente"),
                o.colaborador, o.campo("matricula"), o.campo("funcao"), o.campo("sexo"),
                o.campo("natureza"), o.campo("parteCorpo"),
                num(o.campo("diasAfast")), num(o.campo("diasDeb")),
                o.campo("caracterizacao"), o.campo("contrato"), o.campo("obra"),
                o.campo("horasTrab"), o.campo("tempoEmpresa"), o.campo("idade"),
                o.campo("escolaridade"), o.campo("estadoCivil"),
                o.campo("respAtiv"), o.campo("cargoAtiv"), o.campo("estavaLocal"),
                o.campo("entrevistaSup"), o.campo("descricao"), o.campo("testemunhas"),
                o.campo("perdas"),
                o.campo("materialRel"), o.campo("material"), o.campo("materialAval"),
                o.campo("metodoRel"), o.campo("metodo"), o.campo("metodoAval"),
                o.campo("maquinaRel"), o.campo("maquina"), o.campo("maquinaAval"),
                o.campo("maoObraRel"), o.campo("maoObra"), o.campo("maoObraAval"),
                o.campo("medidaRel"), o.campo("medida"), o.campo("medidaAval"),
                o.campo("meioAmbRel"), o.campo("meioAmb"), o.campo("meioAmbAval"),
                o.campo("conclusaoAnalise"),
                o.statusInv, PlanData.de(o.campo("dataInv")), o.campo("respInv"),
                o.campo("barreiras"), o.campo("causaBarreira"),
                o.campo("causaRaiz"), o.campo("conclusao"), o.campo("licoes"), o.campo("divulgado"),
                comissao(o), o.campo("obs"),
                acoes.size, acoes.count { !it.concluida }, acoes.count { it.vencida(hoje) },
                o.fotos.size, o.createdBy, PlanData.deMs(o.createdAt)
            )
        }
        val aba = PlanKit.abaDados(
            "OCORRÊNCIAS", "Gestão de ocorrências",
            "Gerado em ${PlanData.geradoEm()} · ${ocs.size} ocorrência(s). Filtre pelas setas do cabeçalho: o DASHBOARD acompanha o filtro.",
            cols, linhas, cor = "C62828"
        )

        val acoes = d.acoes.sortedWith(compareBy<AcaoPlano> { it.concluida }.thenBy { it.campo("prazo") })
        val colsAcao = listOf(
            ColPlan("OCORRÊNCIA", 12), ColPlan("TIPO DA OCORRÊNCIA", 24), ColPlan("DATA DA OCORRÊNCIA", 13, TipoCol.DATA),
            ColPlan("PROBLEMA", 34), ColPlan("AÇÃO", 40), ColPlan("TIPO", 12), ColPlan("RESPONSÁVEL", 22),
            ColPlan("DATA PREVISTA", 12, TipoCol.DATA), ColPlan("STATUS", 14), ColPlan("DATA REALIZADA", 13, TipoCol.DATA),
            ColPlan("EFICÁCIA", 12), ColPlan("IMPLANTADA", 12), ColPlan("RESP. VERIFICAÇÃO", 22),
            ColPlan("DATA VERIFICAÇÃO", 14, TipoCol.DATA), ColPlan("EVIDÊNCIA", 34), ColPlan("SITUAÇÃO", 12)
        )
        fun l(t: String) = PlanKit.colRef(colsAcao.indexOfFirst { it.titulo == t })
        val cPrazo = l("DATA PREVISTA")
        val cStatus = l("STATUS")
        fun situacao(a: AcaoPlano) = when {
            a.concluida -> "Concluída"
            a.vencida(hoje) -> "Vencida"
            else -> "No prazo"
        }
        val abaAcoes = PlanKit.abaDados(
            "PLANO DE AÇÃO", "Plano de ação das ocorrências",
            "SITUAÇÃO é recalculada ao abrir: Concluída, Vencida (prazo passou) ou No prazo.",
            colsAcao,
            acoes.map { a ->
                val o = d.ocorrencia(a.ocId)
                listOf(
                    a.ocId, o?.let { tipo(it) }.orEmpty(), PlanData.de(o?.data),
                    a.campo("problema"), a.campo("acao"), a.campo("tipo"), a.campo("resp"),
                    PlanData.de(a.campo("prazo")), a.campo("status"), PlanData.de(a.campo("dataConc")), a.campo("eficacia"),
                    a.campo("implantada"), a.campo("respVerif"), PlanData.de(a.campo("dataVerif")), a.campo("evidencia"),
                    Fx(
                        "IF(LEFT($cStatus{r},6)=\"Conclu\",\"Concluída\",IF(AND(ISNUMBER($cPrazo{r}),$cPrazo{r}<TODAY()),\"Vencida\",\"No prazo\"))",
                        situacao(a)
                    )
                )
            },
            cor = "FFB74D"
        )

        val ultimoAcidente = ocs.filter { it.ehAcidenteAtual(tipo(it)) && Datas.valida(it.data) && it.data <= hoje }.maxOfOrNull { it.data }
        val diasSem = ultimoAcidente?.let { Datas.diff(hoje, it) }
        val kpis = buildList {
            add(KpiPlan("Ocorrências", aba.contar(), ocs.size.toDouble()))
            add(KpiPlan("Acidentes", aba.contarSe("ACIDENTE", "Sim"), ocs.count { it.ehAcidenteAtual(tipo(it)) }.toDouble(), SharedOoxmlWriter.STYLE_CRIT_ALTA))
            if (ultimoAcidente != null && diasSem != null) {
                val (y, m, dd) = ultimoAcidente.split("-").map { it.toInt() }
                add(KpiPlan("Dias sem acidente", "TODAY()-DATE($y,$m,$dd)", diasSem.toDouble(), SharedOoxmlWriter.STYLE_CRIT_BAIXA))
            }
            d.meta?.let { add(KpiPlan("Meta (dias sem acidente)", "$it", it.toDouble())) }
            add(KpiPlan("Dias de afastamento", aba.somar("DIAS DE AFASTAMENTO"), ocs.sumOf { num(it.campo("diasAfast")) ?: 0.0 }))
            add(KpiPlan("Dias debitados", aba.somar("DIAS DEBITADOS"), ocs.sumOf { num(it.campo("diasDeb")) ?: 0.0 }))
            add(
                KpiPlan(
                    "Investigações abertas", "${aba.contar()}-${aba.contarComeca("STATUS DA INVESTIGAÇÃO", "Conclu")}",
                    ocs.count { !it.investigacaoConcluida }.toDouble(), SharedOoxmlWriter.STYLE_CRIT_MEDIA
                )
            )
            add(KpiPlan("Ações do plano", abaAcoes.contar(), acoes.size.toDouble()))
            add(KpiPlan("Ações vencidas", abaAcoes.contarSe("SITUAÇÃO", "Vencida"), acoes.count { situacao(it) == "Vencida" }.toDouble(), SharedOoxmlWriter.STYLE_CRIT_ALTA))
            add(KpiPlan("Ações concluídas", abaAcoes.contarSe("SITUAÇÃO", "Concluída"), acoes.count { it.concluida }.toDouble(), SharedOoxmlWriter.STYLE_CRIT_BAIXA))
        }
        val total = aba.contar()
        val blocos = listOf(
            BlocoPlan("Ocorrências por tipo", "Tipo", PlanKit.contagem(ocs, ::tipo), { aba.contarSe("TIPO", it) }, SharedExcelChartType.PIE),
            BlocoPlan("Ocorrências por classe", "Classe", PlanKit.contagem(ocs, ::classe), { aba.contarSe("CLASSE", it) }),
            BlocoPlan(
                "Ocorrências por mês", "Mês",
                PlanKit.contagem(ocs) { PlanData.mes(PlanData.de(it.data)) }.filter { it.first.isNotBlank() }.sortedBy { it.first }.takeLast(12),
                { aba.contarSe("MÊS", it) }
            ),
            BlocoPlan("Ocorrências por local", "Local", PlanKit.contagem(ocs) { it.local }, { aba.contarSe("LOCAL", it) }, formulaTotal = total),
            BlocoPlan("Ocorrências por cliente", "Cliente", PlanKit.contagem(ocs) { it.campo("empresa") }, { aba.contarSe("EMPRESA", it) }, SharedExcelChartType.PIE, formulaTotal = total, limite = 8),
            BlocoPlan("Causa raiz", "Causa raiz", PlanKit.contagem(ocs) { it.campo("causaRaiz") }, { aba.contarSe("CAUSA RAIZ", it) }),
            BlocoPlan(
                "Dias de afastamento por tipo", "Tipo",
                PlanKit.soma(ocs, ::tipo) { num(it.campo("diasAfast")) ?: 0.0 }, { aba.somarSe("TIPO", it, "DIAS DE AFASTAMENTO") }
            ),
            BlocoPlan("Plano de ação por situação", "Situação", PlanKit.contagem(acoes, ::situacao), { abaAcoes.contarSe("SITUAÇÃO", it) }, SharedExcelChartType.PIE)
        )
        val dash = PlanKit.dashboard(
            "DASHBOARD", "Dashboard — Gestão de Ocorrências",
            "Os números acompanham o filtro das abas OCORRÊNCIAS e PLANO DE AÇÃO. Gerado em ${PlanData.geradoEm()}.",
            kpis, blocos, cor = "C62828"
        )
        return PlanKit.gerar(listOf(dash, aba.sheet, abaAcoes.sheet))
    }
}
